'use client'

import { Plus24 } from '@vinted/monochrome-icons'
import { Cell, Icon, Text } from '@vinted/web-ui'
import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import AddressDetails from 'components/AddressDetails'
import { renderValidation, useFormValidationMessage } from '@marketplace-web/shared/ui-helpers'
import { useTranslate } from '@marketplace-web/shared/i18n'

import { BusinessAddressModel } from 'types/models/business-address'

import { Field } from './types'
import UboAddressModal from './UboAddressModal'

type Props = {
  index: number
  isFormDisabled: boolean
}

const UboAddressDetails = ({ index, isFormDisabled }: Props) => {
  const baseTranslate = useTranslate()
  const translate = useTranslate('kyc_form.business.ubo')

  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false)

  const {
    register,
    getValues,
    control,
    formState: { errors },
  } = useFormContext()
  const validationMessage = useFormValidationMessage(errors)

  const fieldName = `ubos[${index}].${Field.Address}`

  const blankAddressValidationError = baseTranslate('errors.blank', {
    field: translate('address.title'),
  })

  useEffect(() => {
    register(fieldName, { required: blankAddressValidationError })
  }, [fieldName, register, blankAddressValidationError])

  const address = getValues(fieldName)

  function showModal() {
    setIsAddressModalVisible(true)
  }

  function hideModal() {
    setIsAddressModalVisible(false)
  }

  function handleUboAddressModalSubmit(onChange: (...event: Array<unknown>) => void) {
    return (newAddress: BusinessAddressModel) => {
      onChange(newAddress)
      hideModal()
    }
  }

  function renderEmptyAddress() {
    return (
      <>
        <Cell
          styling={Cell.Styling.Wide}
          body={<Text as="h3" type={Text.Type.Subtitle} text={translate('address.title')} />}
        />
        <Cell
          styling={Cell.Styling.Wide}
          type={Cell.Type.Navigating}
          title={translate('address.body')}
          onClick={showModal}
          suffix={<Icon name={Plus24} aria={{ 'aria-hidden': 'true' }} />}
          validation={renderValidation(validationMessage(fieldName))}
          disabled={isFormDisabled}
        />
      </>
    )
  }

  function renderAddressDetails() {
    if (!address) return null

    return (
      <>
        <Cell styling={Cell.Styling.Wide} body={translate('address.title')} />
        <AddressDetails
          styling={Cell.Styling.Wide}
          type={Cell.Type.Navigating}
          showChevron
          address={address}
          onClick={showModal}
          validation={renderValidation(validationMessage(fieldName))}
          isDisabled={isFormDisabled}
        />
      </>
    )
  }

  function renderAddressModal(onChange: (...event: Array<unknown>) => void) {
    return (
      <UboAddressModal
        address={address}
        show={isAddressModalVisible}
        onSubmit={handleUboAddressModalSubmit(onChange)}
        onCancel={hideModal}
      />
    )
  }

  return (
    <>
      {address ? renderAddressDetails() : renderEmptyAddress()}
      <Controller
        control={control}
        name={fieldName}
        defaultValue={null}
        render={({ field: { onChange } }) => renderAddressModal(onChange)}
      />
    </>
  )
}

export default UboAddressDetails
